import { UTILS_COPY } from "@/lib/ui-labels";
import { type ClassValue, clsx } from "clsx"
import { format, parse } from "date-fns"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const CALENDAR_DAY_FORMAT = "yyyy-MM-dd";
export const DATE_TIME_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

export function toCalendarDay(date: Date): string {
  return format(date, CALENDAR_DAY_FORMAT);
}

export function formatDateTime(
  value: string | Date | null | undefined,
  fallback = "-",
  pattern = "MMM d, yyyy h:mm a",
): string {
  if (value == null) return fallback;
  return format(new Date(value), pattern);
}

const TIME_ZONE_UNTIL_TENANT_SETTINGS = "America/New_York";

const EASTERN_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: TIME_ZONE_UNTIL_TENANT_SETTINGS,
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

const EASTERN_CALENDAR_DAY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIME_ZONE_UNTIL_TENANT_SETTINGS,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function easternCalendarDay(value: string | Date): string {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return EASTERN_CALENDAR_DAY_FORMATTER.format(new Date(value));
}

export function formatDateTimeEastern(
  value: string | Date | null | undefined,
  fallback = "-",
): string {
  if (value == null) return fallback;
  const parts = EASTERN_DATE_TIME_FORMATTER.formatToParts(new Date(value));
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("month")} ${get("day")}, ${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")} ${get("timeZoneName")}`;
}

export function formatDate(
  value: string | Date | null | undefined,
  fallback = "-",
  pattern = "MMM d, yyyy",
): string {
  if (value == null) return fallback;
  const dateOnly = typeof value === "string" ? value.slice(0, 10) : toCalendarDay(value);
  return format(parse(dateOnly, CALENDAR_DAY_FORMAT, new Date()), pattern);
}

export function userDisplayName(
  user: { email?: string | null; name?: string | null } | null | undefined,
  fallback: string = UTILS_COPY.unknownUser,
): string {
  return user?.name?.trim() || user?.email?.trim() || fallback;
}
